import React from 'react';

import { IconAlertTriangleFilled } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { Tooltip } from '@cfa/react-components';

import useAuth from '@/hooks/useAuth';

import { mapCategoryToAliasTranslation } from '@/utils/categoryUtils';
import { itemByLanguage } from '@/utils/language';

import Constants, { routes } from '@/constants';

import PlanCard from '../../PlanCard';
import PlanCardLabel from '../../PlanCardComponents/PlanCardLabel';
import PlanCardSubHeader from '../../PlanCardComponents/PlanCardSubHeader';
import PlanCardTitle from '../../PlanCardComponents/PlanCardTitle';
import PlanCardContent from '../../PlanCardContent/PlanCardContent';

import Buttons from './Buttons';
import { enrollmentPassed, useTeamMemberCount } from './utils';

import {
  ComplianceCourseEnrollmentDto,
  listEnrollmentsByCourseOptions,
  Plan,
} from '@/_generated/api-clients/xp';

export interface ComplianceCourseWithUserId extends ComplianceCourseEnrollmentDto {
  userId: string;
}

interface ComplianceCardProps {
  course: Plan;
  onShowEnableCompliancePlan: (id: string, name: string) => void;
}

const ComplianceCard: React.FC<ComplianceCardProps> = ({
  course,
  onShowEnableCompliancePlan,
}) => {
  const { t } = useTranslation();
  const { data: auth } = useAuth();
  const locationsWithAtLeastTrainer =
    auth?.allLocationsWithAtLeastTrainer ?? [];

  const isEnabled = course.status === 'ACTIVE';

  // xp proxy api, need to replace to new xp api when it's ready
  const { data: complianceStatus } = useQuery({
    ...listEnrollmentsByCourseOptions({
      path: { courseId: course.id },
      query: {
        'List of locations to filter': locationsWithAtLeastTrainer.join(),
      },
    }),
    enabled: !!course.id,
  });

  const completedUsers = Object.values(
    complianceStatus?.enrollments ?? {},
  ).filter(enrollmentPassed).length;

  const totalTeamMembers = useTeamMemberCount(
    locationsWithAtLeastTrainer,
    complianceStatus?.enrollments,
  );

  const allUsersCompleted =
    !!totalTeamMembers && completedUsers === totalTeamMembers;
  const percentComplete =
    totalTeamMembers && (completedUsers / totalTeamMembers) * 100;

  const renderInCompleteStatus = () => {
    if (!complianceStatus?.enrollments) {
      return t('Generic.loading');
    }
    if (!totalTeamMembers) {
      return t('Generic.noTeamMembersAssigned');
    }
    return (
      <span className="flex items-center">
        {isEnabled && (
          <Tooltip
            content={t('TrainingPlans.accountability.outOfCompliance')}
            placement="bottom"
          >
            <IconAlertTriangleFilled className="text-feedback-warning-base! mr-1! size-4!" />
          </Tooltip>
        )}
        {`${completedUsers}/${totalTeamMembers} ${t(
          'Generic.teamMembersCompleted',
        )}`}
      </span>
    );
  };

  return (
    <PlanCard
      buttons={
        <Buttons
          plan={course}
          onShowEnableCompliancePlan={onShowEnableCompliancePlan}
        />
      }
      category={Constants.PLAN_CATEGORIES.COMPLIANCE}
      dataTestId={'ManagePlansPlanCard'}
      disabled={!isEnabled}
      link={`${routes.plans.managePlan({ planId: course.id })}?compliancePlan=${itemByLanguage(course.name, '')}`}
      progress={totalTeamMembers && !allUsersCompleted ? percentComplete : null}
    >
      <PlanCardContent>
        <PlanCardLabel>
          {t(
            mapCategoryToAliasTranslation(Constants.PLAN_CATEGORIES.COMPLIANCE),
          )}
        </PlanCardLabel>
        <PlanCardTitle>{itemByLanguage(course.name, '')}</PlanCardTitle>
        {allUsersCompleted ? (
          <PlanCardSubHeader className="success" data-testid="PlanTaskCount">
            {t('Generic.completedByAll')}
          </PlanCardSubHeader>
        ) : (
          <PlanCardSubHeader data-testid="PlanTaskCount">
            {renderInCompleteStatus()}
          </PlanCardSubHeader>
        )}
      </PlanCardContent>
    </PlanCard>
  );
};

interface ComplianceCardWrapperProps {
  course: Plan;
  onShowEnableCompliancePlan: (id: string, name: string) => void;
}

const ComplianceCardWrapper: React.FC<ComplianceCardWrapperProps> = ({
  course,
  ...props
}) => <ComplianceCard {...props} course={course} />;

export default ComplianceCardWrapper;
